import { StateCreator } from 'zustand';
import { BaseAgent } from '../../api/interfaces/baseAgent';
import { EntityStore } from './interfaces/entityStore';
import { notifyError, notifySuccess, notifyInfo } from '../../components/NotificationProvider';
import { Util } from './util';
import { allResults, getOfflineDbTable, OfflineStoreKeys, upsertOfflineDbTableEntry } from '../../offlinestore/offline.util';
import { AxiosError } from 'axios';
import { addToSyncQueue } from '../../offlinestore/syncQueue';

const storeKeyToEndpoint: Record<OfflineStoreKeys, string> = {
    [OfflineStoreKeys.COUNTRIES]: 'countries',
    [OfflineStoreKeys.CUSTOMERS]: 'customers',
    [OfflineStoreKeys.EVENTS]: 'events',
    [OfflineStoreKeys.EVENT_PRODUCTS]: 'event-products',
    [OfflineStoreKeys.EVENT_CUSTOMERS]: 'event-customers',
    [OfflineStoreKeys.ORDER_ITEMS]: 'order-items',
    [OfflineStoreKeys.ORDERS]: 'orders',
    [OfflineStoreKeys.PRODUCTS]: 'products',
    [OfflineStoreKeys.PRODUCT_TYPES]: 'product-types',
    [OfflineStoreKeys.STAND_SECTORS]: 'stand-sectors',
    [OfflineStoreKeys.STANDS]: 'stands',
    [OfflineStoreKeys.STAND_TYPES]: 'stand-types',
    [OfflineStoreKeys.USERS]: 'users'
};

export function createBaseStore<T extends { id: string; eventId?: string }>(agent: BaseAgent, storeKey: OfflineStoreKeys): StateCreator<EntityStore<T>> {
    const apiBaseUrl = process.env.REACT_APP_API_URL || '';
    const endpoint = storeKeyToEndpoint[storeKey] || storeKey;

    return (set) => ({
        items: Array<T>(),
        selectedItem: null as T | null,
        isLoading: false,
        error: null,
        storeKey: storeKey,
        fetchItems: async (params?: any) => {
            set({ isLoading: true, error: null });
            try {
                const items = await agent.getAll(params);
                const newList = navigator.onLine ? await allResults(items, storeKey) : await allResults([], storeKey);
                set({ items: navigator.onLine ? items : newList });
            } catch (error: AxiosError | any) {
                if (navigator.onLine) {
                    if (error?.response?.status !== 401) {
                        notifyError(Util.translate('general.errorOccurred'));
                    }
                } else {
                    const offlineDbTable = getOfflineDbTable<T>(storeKey);
                    const offlineItems = await offlineDbTable.toArray();
                    set({ items: offlineItems });
                }
            } finally {
                set({ isLoading: false });
            }
        },
        createItem: async (item: T) => {
            const now = new Date().toJSON();
            const itemWithTimestamps = { ...item as any, 'createdAt': now, 'updatedAt': now };
            
            try {
                if (navigator.onLine) {
                    await agent.create(item);
                    notifySuccess(Util.translate('general.createSuccess'));
                } else {
                    await addToSyncQueue(
                        `${apiBaseUrl}${endpoint}`,
                        'POST',
                        item,
                        endpoint,
                        item.id,
                        item.eventId
                    );
                    notifyInfo(Util.translate('general.savedOffline'));
                }
                
                await upsertOfflineDbTableEntry(itemWithTimestamps, storeKey);
                set((state) => ({ items: [...state.items, itemWithTimestamps as T] }));
            } catch (error) {
                if (navigator.onLine) {
                    notifyError(Util.translate('general.createFailure'));
                } else {
                    await upsertOfflineDbTableEntry(itemWithTimestamps, storeKey);
                    set((state) => ({ items: [...state.items, itemWithTimestamps as T] }));
                }
            }
        },
        updateItem: async (id: any, updatedData: Partial<T>) => {
            const now = new Date().toJSON();
            const dataWithTimestamp = { ...updatedData, id, 'updatedAt': now };
            
            try {
                if (navigator.onLine) {
                    await agent.update(id, updatedData as any);
                    notifySuccess(Util.translate('general.updateSuccess'));
                } else {
                    await addToSyncQueue(
                        `${apiBaseUrl}${endpoint}/${id}`,
                        'PATCH',
                        updatedData,
                        endpoint,
                        id,
                        (updatedData as any).eventId
                    );
                    notifyInfo(Util.translate('general.savedOffline'));
                }
                
                await upsertOfflineDbTableEntry(dataWithTimestamp as any, storeKey);
                set((state) => ({
                    items: state.items.map((item) => item.id === id ? { ...item, ...updatedData } : item)
                }));
            } catch (error) {
                if (navigator.onLine) {
                    notifyError(Util.translate('general.updateFailure'));
                } else {
                    await upsertOfflineDbTableEntry(dataWithTimestamp as any, storeKey);
                    set((state) => ({
                        items: state.items.map((item) => item.id === id ? { ...item, ...updatedData } : item)
                    }));
                }
            }
        },
        deleteItem: async (id: any) => {
            const offlineDbTable = getOfflineDbTable<T>(storeKey);
            const existingItem = await offlineDbTable.get(id);

            try {
                if (navigator.onLine) {
                    await agent.delete(id);
                    notifySuccess(Util.translate('general.deleteSuccess'));
                } else {
                    await addToSyncQueue(
                        `${apiBaseUrl}${endpoint}/${id}`,
                        'DELETE',
                        null,
                        endpoint,
                        id,
                        existingItem?.eventId
                    );
                    notifyInfo(Util.translate('general.savedOffline'));
                }
                
                await offlineDbTable.delete(id);
                set((state) => ({ items: state.items.filter(item => item.id !== id) }));
            } catch (error) {
                if (navigator.onLine) {
                    notifyError(Util.translate('general.deleteFailure'));
                } else {
                    await offlineDbTable.delete(id);
                    set((state) => ({ items: state.items.filter(item => item.id !== id) }));
                }
            }
        },
        fetchItemById: async (id: any) => {
            const offlineDbTable = getOfflineDbTable<T>(storeKey);
            let item: T | undefined;
            set({ isLoading: true, error: null });

            try {
                if (navigator.onLine) {
                    item = await agent.getById(id);
                    await upsertOfflineDbTableEntry(item as any, storeKey);
                } else {
                    item = await offlineDbTable.get(id);
                }
            } catch (error) {
                item = await offlineDbTable.get(id);
                if (navigator.onLine && !item) {
                    notifyError(Util.translate('general.errorOccurred'));
                }
            } finally {
                set({ selectedItem: item ?? null, isLoading: false });
            }
        },
        setItems: (items: Array<T>) => set({ items: items }),
        setSelectedItem: (item: T | null) => set({ selectedItem: item }),
    });
}

export type FilterProps = {
    filters?: any;
    page?: number;
    pageSize?: number;
    orderBy?: string;
    sortDirection?: string;
};